import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from "redux";
import {Link} from "react-router-dom";
import axios from "axios";
import ListView from "../common/ListView";
import {archiveProduct, searchProducts, showDeleteProductModal} from "./actions";
import {getCategories} from "../Category/actions";
import {getBrands} from "../Brand/actions";
import {getSuppliers} from "../Supplier/actions";
import {Button, Icon, Dropdown, Menu} from "antd";
import DeleteProductFormModal from "./DeleteProductFormModal";
import MoneyDisplay from "../common/MoneyDisplay";
import {authorizationHeaders} from "../utils/api";

class ProductsPage extends Component {
  static propTypes = {
    /* state */
    history: PropTypes.object.isRequired,
    salonId: PropTypes.string.isRequired,
    search: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    brands: PropTypes.array.isRequired,
    suppliers: PropTypes.array.isRequired,
    deleteProductModalVisible: PropTypes.bool.isRequired,
    /* actions */
    searchProducts: PropTypes.func.isRequired,
    getCategories: PropTypes.func.isRequired,
    getBrands: PropTypes.func.isRequired,
    getSuppliers: PropTypes.func.isRequired,
    showDeleteProductModal: PropTypes.func.isRequired,
    archiveProduct: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (this.props.salonId !== prevProps.salonId) this.load();
  }

  load = () => {
    const {salonId, getBrands, getCategories, getSuppliers} = this.props;
    getBrands({salonId});
    getCategories({salonId});
    getSuppliers({salonId});
  };

  onExportAsCSVClick = async() => {
    const {salonId} = this.props;
    // This is a hack so we get feedback from the client
    // don't even bother doing proper redux
    axios({
      url: `${process.env.PUBLIC_URL}/api/v1/r/products?filter[where][salonId]=${salonId}&filter[where][archived]=0`,
      method: 'GET',
      headers: authorizationHeaders(),
      responseType: 'json', // important
    }).then(response => {
      if (!Array.isArray(response.data) || response.data.length === 0) return;
      // massage data into csv
      const keys = Object.keys(response.data[0]).filter(x => !x.endsWith("Id"));
      keys.sort();
      const header = keys.map(camelCaseToWords).join(";");
      const lines = [header];
      for (let row of response.data) {
        const line = keys.map(k => safeCSV(row[k])).join(";");
        lines.push(line);
      }
      return lines.join("\n");
    }).then((csv) => {
      if (!csv) return;
      // download csv
      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products.csv');
      document.body.appendChild(link);
      link.click();
    });
  };

  async archiveProduct({ productId }) {
    try {
      await this.props.archiveProduct({ productId });
      this.listView.refresh();
    } catch (e) {
      console.error(e);
    }
  }

  render() {
    const {
      salonId, history, searchProducts, categories, brands, suppliers, deleteProductModalVisible,
      search: {searching, pagination, filters, sorter, results, searchFilter},
    } = this.props;
    const columns = [
      {
        title: 'Product',
        dataIndex: 'display',
        key: 'display',
        fixed: 'left',
        sorter: true,
        width: 300,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        sorter: true,
        className: 'truncated-column',
      },
      {
        title: 'Category',
        dataIndex: 'categoryName',
        key: 'categoryId',
        sorter: true,
        filters: categories.map(({categoryId: value, name: text}) => ({text, value})),
        render: (categoryName) =>
          <span
            {...({})}
          >
            {categoryName}
          </span>
      },
      {
        title: 'Quantity',
        dataIndex: 'quantityInStock',
        key: 'quantityInStock',
        sorter: true,
        width: 120,
      },
      {
        title: 'Brand',
        dataIndex: 'brandName',
        key: 'brandId',
        sorter: true,
        filters: brands.map(({brandId: value, name: text}) => ({text, value})),
        render: (brandName) =>
          <span
            {...({})}
          >
            {brandName}
          </span>
      },
      {
        title: 'Supplier',
        dataIndex: 'supplierName',
        key: 'supplierId',
        sorter: true,
        filters: suppliers.map(({supplierId: value, name: text}) => ({text, value})),
      },
      {
        title: 'Cost Price',
        dataIndex: 'costPrice',
        key: 'costPrice',
        sorter: true,
        render: price => <MoneyDisplay amount={price} decimals={5}/>
      },
      {
        title: 'Sale Price',
        dataIndex: 'salePrice',
        key: 'salePrice',
        sorter: true,
        render: price => <MoneyDisplay amount={price}/>
      },
      {
        title: 'Actions',
        key: 'action',
        fixed: 'right',
        width: 170,
        render: (_, record) => {
          const encodedId = encodeURIComponent(record.productId);
          return (
            <React.Fragment>
              <Link to={`/product/update/${encodedId}`}>Edit</Link>
              {' | '}
              <Link to={`/product/stocks/${encodedId}`}>Stock</Link>
              {' | '}
              <Dropdown overlay={
                <Menu>
                  <Menu.Item key="0"><Link to={`/product/change-price/${encodedId}`}>Change Price</Link></Menu.Item>
                  <Menu.Item key="1"><Link to={`/product/history/${encodedId}`}>History</Link></Menu.Item>
                  <Menu.Divider/>
                  <Menu.Item key="2"><a onClick={() => this.archiveProduct(record)}>Archive</a></Menu.Item>
                  <Menu.Item key="3"><a onClick={() => this.props.showDeleteProductModal(record)}>Delete</a></Menu.Item>
                </Menu>
              } trigger={['click']}>
                <a className="ant-dropdown-link">
                More <Icon type="down" />
                </a>
              </Dropdown>
            </React.Fragment>
          );
        }
      }
    ];
    return <React.Fragment>
      {deleteProductModalVisible && <DeleteProductFormModal onDelete={() => this.listView.refresh()} />}
      <ListView
        ref={ref => this.listView = ref}
        title="Products"
        searchPlaceholder="Search name, description, brand, category, supplier and barcode"
        loading={searching}
        columns={columns}
        dataSourceProvider={searchProducts}
        dataSource={results}
        pagination={{...pagination, position: 'bottom'}}
        filters={filters}
        sorter={sorter}
        searchFilter={searchFilter}
        salonId={salonId}
        rowKey="productId"
        onAdd={() => history.push('/product/add')}
        extraButtons={[
          {key: 'bulk-add', text: 'Bulk Add', icon: 'plus', onClick: () => history.push('/product/bulk-add')},
          {key: 'import', text: 'Import CSV', icon: 'plus', onClick: () => history.push('/product/import')},
        ]}
        scroll={{x: 1800}}
        footer={(currentPageData) => currentPageData.length && <Button onClick={this.onExportAsCSVClick}>Export as CSV</Button>}
      />
    </React.Fragment>;
  }
}

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
  search: state.product.productsSearch,
  categories: state.category.categories,
  brands: state.brand.brands,
  suppliers: state.supplier.suppliers,
  deleteProductModalVisible: state.product.deleteProductModalVisible,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  searchProducts,
  getCategories,
  getBrands,
  getSuppliers,
  showDeleteProductModal,
  archiveProduct,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPage);

function camelCaseToWords(cc) {
  const words = [];
  let word = '';
  for (let i=0; i<cc.length; i++) {
    if (cc[i] >= 'A' && cc[i] <= 'Z' && word) {
      words.push(word);
      word = cc[i];
    } else {
      word += cc[i];
    }
  }
  if (word) words.push(word);
  const result = words.join(' ');
  return result[0].toUpperCase() + result.substr(1);
}

function safeCSV(value) {
  if (typeof value === 'boolean') return value ? "Yes" : "No";
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string' && value) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return ""; //TODO object (including null), undefined end up here
}



// WEBPACK FOOTER //
// ./src/Product/ProductsPage.js